// @flow

import * as React from 'react';

import { CircleUserRound } from 'lucide-react';

import { connect } from 'react-redux';
import { compose } from 'redux';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectPersonal } from '@gandi/redux-sharing';
import { withTranslator, type TranslatorProps } from '@gandi/translate';

import { Button } from '@gandi/ui';
import { UserInfos, UserInfosItem } from '@gandi/ui/layout';

import { withBasename, type WithBasenameProps } from 'src/helpers/withBasename';
import { withRouter, type WithRouterProps } from 'src/helpers/withRouter';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectUserItems } from 'src/modules/app';
import type { FullState } from 'src/types';

import type { NavigationItems } from '../../types';

import styles from './UserWidgetMobile.scss';

type InjectedProps = {
  // eslint-disable-next-line react/no-unused-prop-types -- used in mapStateToProps
  translator: TranslatorProps,
  ...WithRouterProps<>,
  ...WithBasenameProps,
};

type StateProps = {
  isAuthenticated: boolean,
  items: NavigationItems,
};

export type Props = {
  ...InjectedProps,
  ...StateProps,
};

const mapStateToProps = (
  state: FullState,
  {
    translator: { __ },
    location: { pathname, search },
    basename,
  }: InjectedProps,
): StateProps => ({
  isAuthenticated: selectIsAuthenticatedUser(state),
  items: selectUserItems(state, { __, basename, pathname, search }),
});

function UserWidgetMobile({
  isAuthenticated,
  items,
}: Props): React.Node | Array<React.MixedElement> {
  const { name, email } = useAppSelector(selectPersonal) || {};

  if (!isAuthenticated) {
    return items.map(item => (
      <div className={styles.root} key={`useritem-${item.title}`}>
        <Button href={item.href} to={item.to} title={item.title} block>
          {item.title}
        </Button>
      </div>
    ));
  }

  return (
    <UserInfos className={styles.root}>
      <UserInfosItem withSeparator>
        <div className={styles.userInfoCustomItemCtn}>
          <div className={styles.userNameCtn}>
            <CircleUserRound size="20" />
            <p className={styles.userName}>
              <strong>{name}</strong>
            </p>
          </div>
          <p className={styles.userEmail}>{email}</p>
        </div>
      </UserInfosItem>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const Icon = item.icon;

        return isLast ? (
          //  Log out btn
          <UserInfosItem key={item.title}>
            <div className={styles.btnLogOutCtn}>
              <Button href={item.href} to={item.to} block kind="outline">
                {item.title}
              </Button>
            </div>
          </UserInfosItem>
        ) : (
          <UserInfosItem
            key={item.title}
            href={item.href}
            to={item.to}
            icon={Icon ? <Icon /> : undefined}
          >
            {item.title}
          </UserInfosItem>
        );
      })}
    </UserInfos>
  );
}

export default (compose(
  withRouter,
  withBasename,
  withTranslator({ propsNamespace: 'translator' }),
  connect(mapStateToProps),
)(UserWidgetMobile): React.ComponentType<{}>);
